import { camelCase, forOwn, pick } from 'lodash';

import {
    GetModerationRejectReasonsResult,
    GetModerationRejectReasonsResultItem,
    ModerationRejectExternalReasonsResult
} from '../api/RestApiManager';
import { castStringToEnum } from '../service/CastHelpers';
import { RegionCode } from '../typings/constants';
import {
    ModerationRejectKey,
    ModerationRejectOutdoorReasonsItem,
    ModerationRejectReason,
    ParseExternalModerationRejectReason
} from '../typings/moderation';

const EXTERNAL_ID_KEY = 'external',
    OUTDOOR_ID_KEY = 'outdoor',
    OTHER_REASON_KEY = 'other';
const EXTERNAL_REASON_KEY = 'externalItem';
export function mapModerationEmailContactFromApiFormat(
    data: GetModerationRejectReasonsResult,
    isAgency: boolean
): string | undefined {
    // исключения для менеджеров, согласно DIRECT-44696
    const IGNORED_MANAGERS = [
        'yndx-sedletskaya-manager',
        'yndx-msidenko-direct-manager',
        'yndx-kozhanova-manager',
        'yndx-vecher-manager',
        'yndx-perishkova-manager',
        'yndx-chdv-manager'
    ];

    if (data.manager_info) {
        return data.manager_info.email;
    }

    if (data.agency_info && data.agency_manager) {
        const agencyInfo = data.agency_info;

        // для не агентств используются контакты агентства
        if (!isAgency) {
            return agencyInfo.email;
        }

        // для агентств, менеджеры которых не попали в исключение
        // используются контакты по умолчанию
        if (!IGNORED_MANAGERS.includes(data.agency_manager.login)) {
            const emailByCountryCode = {
                // Беларусь
                [RegionCode.BELORUSSIA]: 'sngmod-agency@support.yandex.ru',
                // Казахстан
                [RegionCode.KAZAKHSTAN]: 'sngmod-agency@support.yandex.ru',
                // Украина
                [RegionCode.UKRAINE]: 'mod-agency@support.yandex.ua'
            };

            return (
                emailByCountryCode[agencyInfo.country_region_id] ||
                'mod-agency@support.yandex.ru'
            );
        }
    }

    return undefined;
}

function sortOutdoorRejectReasonsByOperator(
    reasons: Array<ModerationRejectOutdoorReasonsItem>
): Array<ModerationRejectOutdoorReasonsItem> {
    return reasons.sort((a, b) => {
        if (a.pageName < b.pageName) {
            return -1;
        }

        if (a.pageName > b.pageName) {
            return 1;
        }

        return 0;
    });
}

export function mapModerationRejectReasonsFromApiFormat(
    reasonsList: Array<GetModerationRejectReasonsResultItem>
): Array<ModerationRejectReason> {
    const reasons: Array<ModerationRejectReason> = [],
        reasonsKeys: Array<string> = [],
        otherReasons: Array<string> = [],
        externalReasons: Set<string> = new Set(),
        outdoorReasons: ModerationRejectReason = {
            key: OUTDOOR_ID_KEY,
            reasons: []
        };

    reasonsList.forEach(function(
        reason: GetModerationRejectReasonsResultItem
    ): void {
        if (reason.page_name && reason.page_id && reason.reasons) {
            const outdoorReasonsId: Set<string> = new Set();
            reason.reasons.map(item => outdoorReasonsId.add(item.diag_id));
            outdoorReasons.reasons.push({
                pageName: reason.page_name,
                pageId: reason.page_id,
                externalId: outdoorReasonsId,
                reasons: []
            });
        } else {
            if (reason.token) {
                const token = camelCase(reason.token);

                if (!reasonsKeys.includes(token)) {
                    reasonsKeys.push(token);
                    reasons.push({
                        key: castStringToEnum(token, ModerationRejectKey)
                    });
                }
            } else if (reason.grab_tooltip) {
                externalReasons.add(reason.diag_id);
            } else {
                otherReasons.push(reason.diag_text);
            }
        }
    });

    if (otherReasons.length) {
        reasons.push({
            key: OTHER_REASON_KEY,
            recommendations: otherReasons
        });
    }

    if (externalReasons.size) {
        reasons.push({
            key: EXTERNAL_ID_KEY,
            externalId: externalReasons
        });
    }

    if (outdoorReasons.reasons.length) {
        outdoorReasons.reasons = sortOutdoorRejectReasonsByOperator(
            outdoorReasons.reasons
        );
        reasons.push(outdoorReasons);
    }

    return reasons;
}

// Преобразование данных по внешним причинам отклонения из строки с разметкой и тегами в нужный формат
// Необходимо делать на backend
// author: obi-wan

export function mapExternalModerationRejectReasonFromApiFormat(
    reason: string
): {
    content: string;
    title: string;
} {
    const externalParseObject: ParseExternalModerationRejectReason = JSON.parse(
        reason
    );
    const externalParseObjectTitle = />(.*?)</g.exec(
        // получение заголовка из строки формата '<tag>STRING</tag>'
        externalParseObject.selector['.title']
    );
    if (externalParseObjectTitle) {
        return {
            content: externalParseObject.content,
            title: externalParseObjectTitle[1]
        };
    } else {
        return {
            content: externalParseObject.content,
            title: ''
        };
    }
}

export function mapBannerModerationRejectReasonsFromApiFormat(
    data: GetModerationRejectReasonsResult
): Array<ModerationRejectReason> {
    const bannerReasons = pick(data.banner_diags, [
            'banner',
            'contactinfo',
            'image',
            'sitelinks_set',
            'display_href',
            'video_addition',
            'turbolanding'
        ]),
        calloutReasons = data.callout_diags;

    let outdoorReasons: Array<ModerationRejectExternalReasonsResult> = [];

    const outdoorPages = data.bad_banner_pages;
    if (outdoorPages) {
        outdoorReasons = Object.keys(outdoorPages).map(key => ({
            ...outdoorPages[key],
            page_id: key
        }));
    }

    let reasons: Array<GetModerationRejectReasonsResultItem> = [];

    [bannerReasons, calloutReasons, outdoorReasons].forEach((item: object) => {
        forOwn(
            item,
            (reasonsList: Array<GetModerationRejectReasonsResultItem>) => {
                reasons = reasons.concat(reasonsList);
            }
        );
    });

    return mapModerationRejectReasonsFromApiFormat(reasons);
}

export function mapGroupModerationRejectReasonsFromApiFormat(
    data: GetModerationRejectReasonsResult
): Array<ModerationRejectReason> {
    if (!data.banner_diags || !data.banner_diags.phrases) {
        return [];
    }

    return mapModerationRejectReasonsFromApiFormat(data.banner_diags.phrases);
}

export {
    EXTERNAL_ID_KEY,
    OTHER_REASON_KEY,
    OUTDOOR_ID_KEY,
    EXTERNAL_REASON_KEY
};
